import { Texture } from '../../texture';
import { RasterizedSurfaceMap } from './SurfaceRegionRasterizer';

/**
 * Per-tile surface index + weight GPU textures.
 *
 * **Two-texture split:**
 *   - `indices` — RG8, NEAREST sampling. R = layerA, G = layerB. Integer
 *     surface indices must NOT be filtered (bilinear of e.g. 3 and 7 gives 5,
 *     which is an unrelated surface).
 *   - `weight` — R8, LINEAR sampling. Blend factor from A→B, 0=fully A, 255=fully B.
 *     LINEAR smooths the gradient across the texel grid, eliminating the
 *     blocky boundary that NEAREST sampling on a coarse texel grid produces.
 *
 * Built by SurfaceRegionRasterizer and consumed by the gbuffer-terrain shader
 * via `uSurfaceIndices` + `uSurfaceWeight`.
 */
export class SurfaceIndexMap {
  readonly indicesTex: WebGLTexture;
  readonly weightTex: WebGLTexture;
  /** Stable wrappers — same identity every frame so TextureState cache hits. */
  readonly indicesWrapper: Texture;
  readonly weightWrapper: Texture;
  readonly width: number;
  readonly height: number;

  constructor(gl: WebGL2RenderingContext, map: RasterizedSurfaceMap) {
    const { width, height, indices, weight } = map;
    this.width = width;
    this.height = height;

    // The default UNPACK_ALIGNMENT is 4, which pads each row up to the next
    // multiple of 4 bytes. For R8 (1 byte/pixel) and RG8 (2 bytes/pixel) at
    // non-multiple-of-4 widths (e.g. 125 for a 250m tile at 0.5 tex/m) this
    // makes GL read past the end of our tightly-packed buffer. Force tight
    // packing for both uploads. Default RGBA8 (4 bytes/pixel) never triggered
    // this; the new split-texture encoding does.
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);

    this.indicesTex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.indicesTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RG8, width, height, 0, gl.RG, gl.UNSIGNED_BYTE, indices);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    this.weightTex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.weightTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, width, height, 0, gl.RED, gl.UNSIGNED_BYTE, weight);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    gl.bindTexture(gl.TEXTURE_2D, null);
    // Restore the default so downstream uploads (RGBA images, etc.) aren't
    // affected by our tight-packing state.
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);

    this.indicesWrapper = Texture.fromWebGlTexture(this.indicesTex);
    this.weightWrapper = Texture.fromWebGlTexture(this.weightTex);
  }

  destroy(gl: WebGL2RenderingContext): void {
    gl.deleteTexture(this.indicesTex);
    gl.deleteTexture(this.weightTex);
  }
}
